// Private Home Care Intake Packet — faithful @react-pdf rebuild.
// Content sourced from docs/forms/NONGAPP_packet_source.txt.
// Client Acknowledgments group the four required notices into one signature.

import React from 'react';
import {
  Document, Page, View, Text, styles, ensureCursiveFont,
  Band, Field, Field2, Check, SigBlock, Stamp, Cover, DataTable, PacketPage,
} from './pdfPrimitives';

const AGENCY = 'A Heart For Care (Hard Shoulder Group LLC)';

export function PrivateIntakePacket({ data }) {
  ensureCursiveFont();
  const c = data.client || {};
  const sig = data.signatures || {};
  const f = data.forms || {};
  const D = data.date || '';
  const s = (k) => sig[k] || {};

  return (
    <Document>
      {/* ── COVER ── */}
      <Page size="LETTER" style={styles.page}>
        <Cover
          title="Private Home Care Intake Packet"
          date={D}
          info={[
            ['Client Name:', c.name, 'Client ID:', c.id],
            ['Date of Birth:', c.dob, 'Funding:', c.medicaid || 'Private Pay'],
            ['Responsible Party:', c.guardian, 'Date of Packet:', D],
            ['Care Coordinator:', c.coordinator, 'Packet Type:', 'Private Home Care'],
          ]}
          forms={[
            'Client Information',
            'Service Agreement',
            'Client Acknowledgments (Rights, Consent, HIPAA, Emergency Prep)',
            'Authorization for Release of Medical Information',
            'Intake Assessment (RN-completed)',
            'Service Plan (RN-completed)',
            'Medical Fragility / Complexity Screening (Internal)',
          ]}
        />
      </Page>

      {/* ── CLIENT INFORMATION (HFRD .09(4)(a)(1) record element) ── */}
      <PacketPage>
        <Band title="Client Information"
          subtitle="Rule 111-8-65-.09(4)(a) — Client record identifying information" />
        <Text style={styles.sectionHdr}>CLIENT</Text>
        <Field2 pairs={[['NAME', c.name], ['CLIENT ID', c.id]]} />
        <Field2 pairs={[['DATE OF BIRTH', c.dob], ['GENDER', c.gender]]} />
        <Field label="ADDRESS" value={c.address} />
        <Field2 pairs={[['PHONE', c.phone], ['PRIMARY LANGUAGE', c.language]]} />
        <Field label="FUNDING / INSURANCE" value={c.insurance} />

        <Text style={styles.sectionHdr}>RESPONSIBLE PARTY / LEGAL REPRESENTATIVE</Text>
        <Field2 pairs={[['NAME', c.responsibleParty], ['RELATIONSHIP', c.responsibleRelationship]]} />
        <Field2 pairs={[['PHONE', c.responsiblePhone], ['EMAIL', c.email]]} />

        <Text style={styles.sectionHdr}>EMERGENCY CONTACT</Text>
        <Field2 pairs={[['NAME', c.emergencyName], ['RELATIONSHIP', c.emergencyRelationship]]} />
        <Field label="PHONE" value={c.emergencyPhone} />

        <Text style={styles.sectionHdr}>MEDICAL</Text>
        <Field label="Primary Diagnosis" value={c.diagnosis} />
        <Field label="Allergies / Precautions" value={c.allergies} />
        <Field label="Current Medications" value={c.medications} />
        <Field2 pairs={[['PRIMARY PHYSICIAN', c.physician], ['PHYSICIAN PHONE', c.physicianPhone]]} />
        <Field label="Physician Address" value={c.physicianAddress} />

        <Text style={styles.sectionHdr}>REFERRAL</Text>
        <Field2 pairs={[['REFERRAL DATE', c.referralDate], ['REFERRAL SOURCE', c.referralSource]]} />

        <Text style={[styles.legal, { marginTop: 10 }]}>
          This information is drawn from the client record and is maintained as part of the client file in
          accordance with Rule 111-8-65-.09(4)(a).
        </Text>
      </PacketPage>

      {/* ── SERVICE AGREEMENT ── */}
      <PacketPage>
        <Band title="Service Agreement"
          subtitle="Rule 111-8-65-.09(2)(a) — Services, frequency, duration, rate of charges, client rights acknowledgment" />
        <Field2 pairs={[['CLIENT NAME', c.name], ['CLIENT ID', c.id]]} />
        <Field2 pairs={[['DATE OF BIRTH', c.dob], ['SERVICE START', f.serviceStart]]} />
        <Field2 pairs={[['INITIAL CONTACT', f.initialContact], ['CARE COORDINATOR', c.coordinator]]} />

        <Text style={styles.sectionHdr}>CLIENT'S REQUEST FOR SERVICE</Text>
        <Text style={styles.body}>{f.serviceRequest || ' '}</Text>

        <Text style={styles.sectionHdr}>DESCRIPTION OF SERVICES — Hard Shoulder Group LLC agrees to provide:</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          {[
            'Nursing Services', 'Personal Care Services', 'Companion / Sitter Services',
            'Homemaker Services', 'Respite Services', 'Aide Services',
            'Personal Support Services', 'Personal Care Aide Services',
          ].map((svc) => (
            <View key={svc} style={{ width: '50%' }}>
              <Check checked={(f.services || []).includes(svc)} label={svc} />
            </View>
          ))}
        </View>
        <Field2 pairs={[['FREQUENCY & DURATION', f.frequency], ['RATE OF SERVICE', f.rate]]} />

        <Text style={styles.sectionHdr}>BILLING & PAYMENT</Text>
        <Text style={styles.legal}>
          If the client is eligible for Medicaid, Hard Shoulder Group LLC agrees to accept Medicaid as full
          payment (or cost-share, if applicable). If the client becomes ineligible, the client agrees to pay
          privately. An invoice will be sent at the end of each month; payment is due by the 5th of the
          following month. Rates are subject to periodic adjustment with a minimum of 30 days' written notice.
        </Text>
        <Text style={styles.legal}>
          <Text style={{ fontFamily: 'Helvetica-Bold' }}>Client's Right to Cancel: </Text>
          The client may cancel at any time and will be charged only for services actually rendered prior to
          notice. The agency requests at least 7 days' advance written notice when possible.
        </Text>
        <Text style={styles.legal}>
          <Text style={{ fontFamily: 'Helvetica-Bold' }}>Personal Funds & Property: </Text>
          The agency will not accept responsibility for funds, valuables, or personal items, and personnel may
          not enter into financial transactions with clients.
        </Text>
        <Text style={styles.legal}>
          <Text style={{ fontFamily: 'Helvetica-Bold' }}>Electronic Signatures: </Text>
          The agency accepts electronic signatures in accordance with the ESIGN Act, UETA, and HIPAA.
        </Text>
        <Text style={styles.legal}>
          <Text style={{ fontFamily: 'Helvetica-Bold' }}>Term & Termination: </Text>
          Either party may terminate with at least 7 days' written notice. Immediate termination may occur for
          non-payment, unsafe environment, or client condition exceeding agency capabilities.
        </Text>

        <SigBlock signers={[
          { label: 'Client / Responsible Party Name (Print) / Signature', name: s('form4_client').name, date: s('form4_client').date },
          { label: 'Agency Representative / Signature', name: s('form4_agency').name, date: s('form4_agency').date },
        ]} />
        <Stamp date={D} />
      </PacketPage>

      {/* ── CLIENT ACKNOWLEDGMENTS ── */}
      <PacketPage>
        <Band title="Client Acknowledgments"
          subtitle="Rights & Responsibilities · Consent · HIPAA · Emergency Preparedness" />
        <Text style={styles.body}>
          By signing below, I acknowledge receipt of and agreement with each of the following notices and
          consents, each of which has been provided to me in full:
        </Text>
        {[
          ['Client Rights & Responsibilities',
            'I have received and understand the Client Rights & Responsibilities notice, including my right to participate in my care plan, refuse services, submit complaints without retaliation, and contact the Georgia DCH Private Home Care Provider Unit at 1-800-878-6442.'],
          ['Consent to Provide Services',
            'I voluntarily authorize Hard Shoulder Group LLC (DBA A Heart for Care) to provide home care services as outlined in my individualized service plan. I may refuse or discontinue services at any time, and all caregivers have been trained per Georgia DCH regulations.'],
          ['HIPAA Privacy Notice',
            'I have received and reviewed the HIPAA Privacy Notice. I understand how my PHI may be used for treatment coordination, billing, and legal compliance, and my rights to access, correct, and restrict my health information.'],
          ['Emergency Preparedness',
            'I have received the Emergency Preparedness information, including agency emergency contacts (470-258-2030 and 404-671-2732), my responsibilities during emergencies, and local emergency numbers. I understand to call 911 first in any life-threatening situation.'],
        ].map(([title, body]) => (
          <View key={title}>
            <Text style={styles.formSub}>{title}</Text>
            <Text style={styles.legal}>{body}</Text>
            <View style={{ borderBottomWidth: 0.5, borderBottomColor: '#C8D8F0', marginVertical: 4 }} />
          </View>
        ))}
        <Text style={[styles.body, { marginTop: 8 }]}>
          I acknowledge receipt of all four notices listed above and have had an opportunity to ask questions.
        </Text>
        <SigBlock signers={[
          { label: 'Client / Responsible Party Name (Print) / Signature', name: s('ack_client').name, date: s('ack_client').date },
        ]} />
        <Stamp date={D} />
      </PacketPage>

      {/* ── AUTHORIZATION FOR RELEASE OF MEDICAL INFORMATION ── */}
      <PacketPage>
        <Band title="Authorization for Release of Medical Information" subtitle="PHI Release — HIPAA Compliant" />
        <Field2 pairs={[['PATIENT NAME', c.name], ['DATE OF BIRTH', c.dob]]} />
        <Field2 pairs={[['MEDICAID ID', c.medicaid], ['PHONE', c.phone]]} />
        <Field2 pairs={[['GUARDIAN NAME', c.guardian], ['RELATIONSHIP', c.relationship]]} />
        <Field label="EMAIL" value={c.email} />
        <Field label="ADDRESS" value={c.address} />
        <View style={{ height: 6 }} />
        <Text style={styles.bodyJust}>
          I, the undersigned, hereby authorize <Text style={{ fontFamily: 'Helvetica-Bold' }}>{AGENCY}</Text> to
          request, access, receive, and send the medical records and protected health information (PHI) of the
          patient named above, from any and all healthcare providers, physicians, specialists, hospitals,
          pharmacies, therapy providers, and the Georgia Department of Community Health, as necessary for the
          coordination of home care services.
        </Text>
        <Text style={styles.legal}>
          I understand this authorization is voluntary and may be revoked in writing at any time. Revocation
          does not affect disclosures already made.
        </Text>
        <Text style={styles.sectionHdr}>INFORMATION TO BE RELEASED:</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          {['Medical history/diagnoses', 'Physician orders/POC', 'Nursing notes', 'Medications',
            'Discharge summaries', 'Therapy records', 'Lab/imaging results', 'GAPP documentation'
          ].map((item) => (
            <View key={item} style={{ width: '50%' }}>
              <Check checked={(f.phiReleased || []).includes(item)} label={item} />
            </View>
          ))}
        </View>
        <Text style={[styles.body, { marginTop: 6 }]}>
          EXPIRATION: Authorization valid until <Text style={{ fontFamily: 'Helvetica-Bold' }}>end of home care services</Text>.
        </Text>
        <Text style={styles.body}>I am the: <Text style={{ fontFamily: 'Helvetica-Bold' }}>{c.relationship || 'Parent / Legal Guardian'}</Text></Text>
        <SigBlock signers={[
          { label: 'Signature of Patient or Authorized Representative', name: s('phi_rep').name, date: s('phi_rep').date },
          { label: 'AHFC Staff Witness Signature', name: s('phi_witness').name, date: s('phi_witness').date },
        ]} />
        <Stamp date={D} />
      </PacketPage>

      {/* ── INTAKE ASSESSMENT (RN) ── */}
      <PacketPage>
        <Band title="Intake Assessment"
          subtitle="Rule 111-8-65-.09 & .11 — Completed by or under supervision of a Registered Nurse" />
        <Field2 pairs={[['CLIENT NAME', c.name], ['CLIENT DOB', c.dob]]} />
        <Field2 pairs={[['CLIENT ID', c.id], ['SERVICE START', f.serviceStart]]} />
        <Field2 pairs={[['REFERRAL DATE', f.referralDate], ['REFERRAL SOURCE', f.referralSource]]} />

        <Text style={styles.sectionHdr}>Activities of Daily Living (ADLs)</Text>
        <DataTable
          headers={['Activity', 'Status', 'Comments']}
          widths={['28%', '24%', '48%']}
          rows={(f.adls || []).map((r) => [r.activity, r.status, r.comments || ''])}
        />
        <Text style={styles.sectionHdr}>Instrumental Activities of Daily Living (IADLs)</Text>
        <DataTable
          headers={['Activity', 'Status', 'Comments']}
          widths={['28%', '24%', '48%']}
          rows={(f.iadls || []).map((r) => [r.activity, r.status, r.comments || ''])}
        />
        <Text style={styles.sectionHdr}>Environment & Safety</Text>
        <Field label="Home Accessibility" value={f.homeAccessibility} />
        <Field label="Fall Risks" value={f.fallRisks} />
        <Field label="Special Equipment" value={f.specialEquipment} />
        <Field label="Other Safety Concerns" value={f.otherSafety} />
        <Text style={styles.sectionHdr}>Medical / Nursing Needs</Text>
        <DataTable
          headers={['Service', 'Required?', 'Notes']}
          widths={['34%', '14%', '52%']}
          rows={(f.nursingNeeds || []).map((r) => [r.service, r.required, r.notes || ''])}
        />
        <Text style={styles.sectionHdr}>Risk Assessment</Text>
        <DataTable
          headers={['Risk Type', 'At Risk?', 'Notes / Mitigation Strategy']}
          widths={['26%', '14%', '60%']}
          rows={(f.risks || []).map((r) => [r.type, r.atRisk, r.notes || ''])}
        />
        <Text style={styles.sectionHdr}>Assessment Summary & Plan</Text>
        <Text style={styles.body}>{f.assessmentSummary || ' '}</Text>
        <SigBlock signers={[
          { label: 'RN / Qualified Professional Name (Print) / Signature', name: s('form2_rn').name, date: s('form2_rn').date },
          { label: 'Client / Responsible Party Name (Print) / Signature', name: s('form2_client').name, date: s('form2_client').date },
        ]} />
        <Stamp date={D} />
      </PacketPage>

      {/* ── SERVICE PLAN (RN) ── */}
      <PacketPage>
        <Band title="Service Plan"
          subtitle="Rule 111-8-65-.11 — Completed within 7 working days; nursing plans reviewed every 62 days" />
        <Field2 pairs={[['CLIENT NAME', c.name], ['CLIENT DOB', c.dob]]} />
        <Field2 pairs={[['SERVICE START', f.serviceStart], ['PREPARED BY (RN)', f.preparedBy]]} />

        <Text style={styles.sectionHdr}>Client Profile, Preferences & Daily Routine</Text>
        <Field2 pairs={[['PRIMARY DIAGNOSIS', f.primaryDx], ['ALLERGIES', f.allergies]]} />
        <Field2 pairs={[['FUNCTIONAL LIMITS', f.functionalLimits], ['ASSISTIVE DEVICES', f.assistiveDevices]]} />

        <Text style={styles.sectionHdr}>Goals & Outcomes</Text>
        <DataTable
          headers={['#', 'Client-Centered Goal', 'Desired Outcome', 'Timeline']}
          widths={['8%', '37%', '37%', '18%']}
          rows={(f.goals || []).map((g, i) => [String(i + 1), g.goal, g.outcome, g.timeline])}
        />
        <Text style={styles.sectionHdr}>Services to Be Provided</Text>
        <DataTable
          headers={['Service Type', 'Frequency', 'Duration', 'Provider Role']}
          widths={['32%', '24%', '20%', '24%']}
          rows={(f.planServices || []).map((sv) => [sv.type, sv.frequency, sv.duration, sv.role])}
        />
        <Text style={styles.sectionHdr}>Consent & Acknowledgment</Text>
        <Text style={styles.legal}>
          By signing below, I acknowledge that I have reviewed and agree to the service plan developed above. I
          understand services will be provided based on this plan and reviewed regularly for updates.
        </Text>
        <SigBlock signers={[
          { label: 'Client / Responsible Party Name (Print) / Signature', name: s('form3_client').name, date: s('form3_client').date },
          { label: 'RN / Qualified Professional Name (Print) / Signature', name: s('form3_rn').name, date: s('form3_rn').date },
        ]} />
        <Stamp date={D} />
      </PacketPage>

      {/* ── MEDICAL FRAGILITY / COMPLEXITY SCREENING (INTERNAL) ── */}
      <PacketPage>
        <Band title="Medical Fragility / Complexity Screening"
          subtitle="Rule 111-8-65-.03(l) — Internal determination, completed by RN & reviewed by Administrator" />
        <Field2 pairs={[['CLIENT NAME', c.name], ['CLIENT DOB', c.dob]]} />
        <Field2 pairs={[['ASSESSMENT DATE', f.mfmcDate], ['ASSESSED BY (RN)', f.mfmcAssessor]]} />

        <Text style={styles.sectionHdr}>Criteria Checklist</Text>
        <DataTable
          headers={['Indicator', 'Present?', 'Supporting Evidence / Notes']}
          widths={['44%', '14%', '42%']}
          rows={(f.mfmcCriteria || []).map((r) => [r.indicator, r.present, r.notes || ''])}
        />
        <Text style={styles.sectionHdr}>Clinical Narrative</Text>
        <Text style={styles.body}>{f.mfmcNarrative || ' '}</Text>
        <Text style={styles.sectionHdr}>Determination</Text>
        <Check checked={f.mfmcDetermination === 'yes'} label="Medically Frail / Medically Compromised" />
        <Check checked={f.mfmcDetermination === 'no'} label="NOT Medically Frail / Medically Compromised" />
        <SigBlock signers={[
          { label: 'RN / Qualified Professional Name (Print) / Signature', name: s('form9_rn').name, date: s('form9_rn').date },
          { label: 'Administrator / Director Name (Print) / Signature', name: s('form9_admin').name, date: s('form9_admin').date },
        ]} />
        <Stamp date={D} />
      </PacketPage>
    </Document>
  );
}

export default PrivateIntakePacket;
